<template>
  <div>
    <Card>
      <Spinner v-if="loading" />

      <div v-else>
        <template v-if="campaign.config && campaign.config.userPreferences.onlyInformativeCampaign">
          <FiltersSet
            @filterChanged="handleFilterChanged"
            ref="section"
            v-model="filters"
            :all-brands="allBrands"
            :brands="getSelectedBrands()"
            :config="localConfig.filters"
            :isCreate="isCreate"
            :isEdit="isEdit"
            :loading="loading"
            :userPreferences="campaign.config.userPreferences" />
        </template>
        <template v-else>

          <TabSet @updateTabIndex="selectedTab = $event.index" tab-set-id="offers_tabs" ref="tabSet">
            <Tab v-for="(section, index) in Object.keys(filters)" :key="index" :position="`${index}`" :label="getSectionTitle(section)" :animation-name="currentAnimation" :id="section" tab-set-id="offers_tabs">
              <FiltersSet
                @applyToOtherSections="handleApplyToOtherSections"
                @filterChanged="handleFilterChanged($event, section)"
                v-model="filters[section]"
                :all-brands="allBrands"
                :brands="getSelectedBrands(section)"
                :config="localConfig.filters[section]"
                :isCreate="isCreate"
                :isEdit="isEdit"
                :loading="loading"
                :key="index"
                :original-values="getOriginalValues(section)"
                :ref="`section_${section}`"
                :section-number="section"
                :userPreferences="campaign.config.userPreferences" />
            </Tab>
          </TabSet>
        </template>
      </div>
    </Card>
  </div>
</template>
<script>
import _ from 'lodash'
import { getAllData, getEmptyConfigByKey as getEmptyConfig } from '../../utils/indexedDbUtils'
import FiltersSet from './target-definition/FiltersSet'
import Card from '../../slds/Card'
import HasMode from '../../utils/HasMode'
import Spinner from '../../Spinner.vue'
import TabSet from '../../slds/tabs/TabSet'
import Tab from '../../slds/tabs/Tab'

const fieldsToClone = [
  'associatedDealers',
  'clientType',
  'deduplication',
  'engagementConditions',
  'maintenanceModelCluster',
  'marketingPrivacyConsent',
  'mileageTag',
  'monthsFromFirstRegistrationDate',
  'orchestration',
  'profilingPrivacyConsent',
  'purchasedServicesAndAccessories',
  'registrationOnMOC',
  'warrantyExtension',
  'warrantyExtensionInclude',
  'zipCodes'
]

export default {
  name: 'TargetDefinition',
  mixins: [HasMode],
  props: {
    config: Object,
    loading: Boolean,
    mode: String
  },
  components: {
    Tab,
    TabSet,
    Card,
    FiltersSet,
    Spinner
  },
  data () {
    return {
      allbrands: [],
      campaign: {},
      currentAnimation: 'slide-fade-right-delay',
      dealersMap: {},
      hasCustomValidation: true,
      localConfig: {},
      openSection: '',
      selectedTab: -1,
      filters: {}
    }
  },
  async activated () {
    this.$emit('startLoading')
    this.dealersMap = (await getAllData('clusters')).reduce((acc, val) => {
      acc[val.cluster] = val
      return acc
    }, {})
    this.callLoadFromStore()
    const emptyConfig = await getEmptyConfig('FULL')
    this.allBrands = emptyConfig.config.filters.brands.options
    this.$emit('endLoading')
  },
  methods: {
    restoreFullOffers (lightOffers) {
      const fullOffers = Object.values(this.$store.getters['campaignDesigner/state'].offers).flat()
      const offers = []

      let mapped
      if (lightOffers[0] && lightOffers[0].sectionId) {
        mapped = lightOffers
      } else {
        mapped = lightOffers.reduce((acc, val) => {
          acc.push(Object.values(val).flat())
          return acc.flat()
        }, [])
      }

      mapped.forEach(lightOffer => {
        const fullOffer = fullOffers.find(fullOffer => fullOffer.sectionId === lightOffer.sectionId)

        if (fullOffer) {
          offers.push({ ...fullOffer, tags: lightOffer.tags, contentPriority: lightOffer.contentPriority })
        }
      })

      return offers
    },
    getSelectedBrands (section) {
      if (section && typeof section === 'object') {
        let offers
        if (this.campaign.config.offers[0] && this.campaign.config.offers[0].sectionId) {
          offers = this.campaign.config.offers
        } else {
          offers = this.restoreFullOffers(this.campaign.config.offers)
        }
        const offer = offers.find(o => o.sectionId === section)
        if (offer.brand === 'All') {
          return this.campaign.config.campaignData.campaignBrands.selected
        } else {
          return [offer.brand]
        }
      } else {
        return this.$store.getters['campaignDesigner/state'].currentCampaign.brands
      }
    },
    callLoadFromStore () {
      const campaign = this.$store.getters['campaignDesigner/state'].currentCampaign
      this.campaign = campaign
      this.localConfig = _.cloneDeep(campaign.config)
      this.filters = _.cloneDeep(campaign.config.filters)
      if (!campaign.config.userPreferences.onlyInformativeCampaign) {
        this.openSection = Object.keys(this.filters)[0]
      }
    },
    getOriginalValues (section) {
      let offers
      if (this.campaign.config.offers[0] && this.campaign.config.offers[0].sectionId) {
        offers = this.campaign.config.offers
      } else {
        offers = this.restoreFullOffers(this.campaign.config.offers)
      }
      const offer = offers.find(offer => offer.sectionId === section)
      const { kmFrom, kmTo, range } = offer
      return { kmFrom, kmTo, range }
    },
    getSectionTitle (section) {
      return this.campaign.config.filters[section].sectionTitle
    },
    getSectionToSave () {
      return {
        config: this.filters,
        userPreferences: {
          privacyConsentEdited: true
        }
      }
    },
    handleApplyToOtherSections (event) {
      const { value, sectionNumber } = event

      Object.keys(_.omit(this.filters, sectionNumber)).forEach(section => {
        fieldsToClone.forEach(field => {
          if (value.hasOwnProperty(field)) {
            const o = { fieldName: field, newValue: _.cloneDeep(value[field]) }
            this.handleFilterChanged(o, section)
            this.$refs[`section_${section}`][0][field] = _.cloneDeep(value[field])
          }
        })
      })
    },
    handleFilterChanged ({ fieldName, newValue }, section) {
      let filterSet
      let filterSetRef
      if (section) {
        filterSet = this.filters[section]
        filterSetRef = this.$refs[`section_${section}`][0]
      } else {
        filterSet = this.filters
        filterSetRef = this.$refs.section
      }

      if (filterSet[fieldName] !== undefined && filterSet[fieldName].selected !== undefined) {
        filterSet[fieldName].selected = newValue
      } else {
        filterSet[fieldName] = newValue
      }

      // Additional handlers
      switch (fieldName) {
        case 'dealerCluster':
          if (newValue && newValue.length && !filterSetRef.dealers) {
            filterSetRef.dealers = {}
            newValue.forEach(dealerCluster => {
              filterSetRef.dealers[dealerCluster.toString()] = [...new Set(this.dealersMap[dealerCluster].dealers.map(dealer => dealer.idDealer))]
            })
          } else {
            const isAdded = newValue.length > Object.keys(filterSetRef.dealers || []).length
            let source, target

            if (isAdded) {
              source = newValue.map(v => v.toString())
              target = Object.keys(filterSetRef.dealers || [])
            } else {
              source = Object.keys(filterSetRef.dealers || [])
              target = newValue.map(v => v.toString())
            }

            const difference = _.differenceWith(source, target, _.isEqual)
            difference.forEach(cluster => {
              if (isAdded) filterSetRef.dealers[cluster] = [...new Set(this.dealersMap[cluster].dealers.map(dealer => dealer.idDealer))]
              else delete filterSetRef.dealers[cluster]
            })
          }

          break
      }
    },
    doValidate () {
      if (this.campaign.config.userPreferences.onlyInformativeCampaign) {
        return this.$refs.section.validate()
      } else {
        let valid = true
        let firstInvalidTab
        Object.entries(this.$refs)
          .filter(e => e[0].includes('section'))
          .forEach(ref => {
            ref[1].forEach(subRef => {
              const isSectionValid = !!(subRef.validate())
              if (!isSectionValid && !firstInvalidTab) {
                firstInvalidTab = subRef.$props.sectionNumber
              }
              this.$refs.tabSet.setErrorTab(parseInt(subRef.$props.sectionNumber), !isSectionValid)
              valid &= isSectionValid
            })
          })

        if (firstInvalidTab) {
          const index = Object.keys(this.filters).indexOf(firstInvalidTab)
          this.$refs.tabSet.setActiveTab(index)
        }

        return valid
      }
    },
    validate () {
      return this.doValidate()
    }
  },
  watch: {
    selectedTab (newTab, oldTab) {
      if (newTab > oldTab) {
        this.currentAnimation = 'slide-fade-left-delay'
      } else {
        this.currentAnimation = 'slide-fade-right-delay'
      }
    },
    openSection (newSection, oldSection) {
      const newIndex = this.campaign.config.offers.findIndex(offer => offer.sectionId === newSection)
      const oldIndex = this.campaign.config.offers.findIndex(offer => offer.sectionId === oldSection)

      if (newIndex > oldIndex) {
        this.currentAnimation = 'slide-fade-left'
      } else {
        this.currentAnimation = 'slide-fade-right'
      }
    }
  }
}
</script>
<style lang="scss" scoped>
  .slds-card {
    height: 100%;
    overflow-y: auto;
  }
</style>
